if (!customElements.get('pp-plant-recommendations')) {
  customElements.define('pp-plant-recommendations', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.active = 'plants';
      this.tabs = [...this.querySelectorAll('[data-rec-tab]')];
      this.panels = [...this.querySelectorAll('[data-rec-panel]')];
      this.tracks = [...this.querySelectorAll('[data-rec-track]')];
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-rec-tab]');
        if (tab) this.select(tab.dataset.recTab);
        const arrow = event.target.closest('[data-rec-scroll]');
        if (arrow) this.track.scrollBy({left: Number(arrow.dataset.recScroll) * this.track.clientWidth * .85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }, options);
      this.addEventListener('keydown', event => {
        const tab = event.target.closest('[data-rec-tab]');
        if (!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        const visible = this.tabs.filter(t => !t.hidden), index = visible.indexOf(tab);
        let next = (index + (event.key === 'ArrowLeft' ? -1 : 1) + visible.length) % visible.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = visible.length - 1;
        event.preventDefault(); this.select(visible[next].dataset.recTab); visible[next].focus();
      }, options);
      this.tracks.forEach(track => track.addEventListener('scroll', () => this.updateArrows(), options));
      this.resize = new ResizeObserver(() => this.updateArrows()); this.tracks.forEach(track => this.resize.observe(track));
      document.addEventListener('pp:pots-updated', event => {
        if (String(event.detail?.productId) === this.dataset.productId) this.updatePots();
      }, options);
      this.updatePots(); this.select('plants');
      this.loadObserver?.disconnect();
      if (this.dataset.needsRecommendations === 'true') {
        if ('IntersectionObserver' in window) {
          this.loadObserver = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return;
            this.loadObserver.disconnect(); this.loadPlants();
          }, {rootMargin: '400px 0px'});
          this.loadObserver.observe(this);
        } else this.loadPlants();
      }
    }
    get track() {return this.querySelector(`[data-rec-track="${this.active}"]`);}
    select(key) {
      if (this.tabs.find(t => t.dataset.recTab === key)?.hidden) key = 'plants';
      this.active = key;
      this.tabs.forEach(tab => {const active = tab.dataset.recTab === key; tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1;});
      this.panels.forEach(panel => {panel.hidden = panel.dataset.recPanel !== key;});
      this.updateArrows();
    }
    updateArrows() {
      if (!this.track) return;
      const {scrollLeft, scrollWidth, clientWidth} = this.track;
      this.querySelector('.pp-recommendations-arrows').hidden = scrollWidth <= clientWidth + 2;
      this.querySelector('[data-rec-scroll="-1"]').disabled = scrollLeft <= 1;
      this.querySelector('[data-rec-scroll="1"]').disabled = scrollLeft + clientWidth >= scrollWidth - 2;
    }
    updatePots() {
      const source = document.querySelector(`pp-plant-options[data-product-id="${this.dataset.productId}"]`);
      const inputs = source ? [...source.querySelectorAll('input[data-group="pot"]:not(:disabled)')] : [];
      const seen = new Set();
      const cards = inputs.filter(input => {
        if (!input.dataset.recommendationUrl || seen.has(input.value)) return false;
        seen.add(input.value); return true;
      }).map(input => {
        const card = document.createElement('a'); card.className = 'pp-recommendation-card'; card.href = input.dataset.recommendationUrl;
        const media = document.createElement('div'); media.className = 'pp-recommendation-image';
        if (input.dataset.recommendationImage) {
          const img = document.createElement('img'); img.src = input.dataset.recommendationImage; img.alt = input.dataset.recommendationTitle || ''; img.loading = 'lazy'; img.width = 600; img.height = 600; media.append(img);
        }
        const title = document.createElement('h3'); title.textContent = input.dataset.recommendationTitle;
        const variant = document.createElement('p'); variant.className = 'pp-recommendation-variant'; variant.textContent = input.dataset.recommendationVariant;
        const price = document.createElement('p'); price.className = 'pp-recommendation-price';
        const cents = Number(input.dataset.price);
        price.textContent = new Intl.NumberFormat(this.dataset.locale || 'da', {style:'currency',currency:this.dataset.currency || 'DKK',minimumFractionDigits:cents % 100 === 0 ? 0 : 2,maximumFractionDigits:2}).format(cents/100);
        card.append(media,title); if (variant.textContent) card.append(variant); card.append(price); return card;
      });
      const track = this.querySelector('[data-rec-track="pots"]');
      const focused = track.contains(document.activeElement);
      track.replaceChildren(...cards); track.scrollLeft = 0;
      const potTab = this.tabs.find(t => t.dataset.recTab === 'pots');
      const tabFocused = document.activeElement === potTab;
      potTab.hidden = !cards.length;
      if (!cards.length && this.active === 'pots') this.select('plants');
      if (focused || (tabFocused && !cards.length)) this.tabs.find(t => t.dataset.recTab === this.active).focus();
      this.querySelector('[data-rec-status]').textContent = this.active === 'pots' ? `${cards.length} potteskjulere til den valgte størrelse` : '';
      this.updateArrows();
    }
    async loadPlants() {
      const url = new URL(this.dataset.url, location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('product_id', this.dataset.productId);
      url.searchParams.set('limit', '8'); url.searchParams.set('intent','related');
      const panel = this.querySelector('[data-rec-panel="plants"]'); panel.setAttribute('aria-busy','true');
      try {
        const response = await fetch(url, {signal:this.abort.signal}); if (!response.ok) throw new Error('Recommendations unavailable');
        const html = new DOMParser().parseFromString(await response.text(),'text/html');
        const track = html.querySelector('[data-rec-track="plants"]');
        if (!track || !this.isConnected) return;
        this.querySelector('[data-rec-track="plants"]').replaceChildren(...[...track.children].map(node => document.importNode(node,true)));
        this.querySelector('[data-rec-empty]').hidden = track.children.length > 0;
      } catch (error) {
        // The existing browse link remains available on a failed/empty response.
      } finally { panel.removeAttribute('aria-busy'); this.updateArrows(); }
    }
    disconnectedCallback() {this.loadObserver?.disconnect();this.abort?.abort();this.resize?.disconnect();}
  });
}
